import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, User, AtSign, Lock, Eye, EyeOff, ArrowRight, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import NexusLogo from "@/components/NexusLogo";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ email: "", username: "", display_name: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Conta criada! Bem-vindo à Nexus Live.");
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,520px)_1fr] bg-background text-foreground">
      {/* LEFT — Form */}
      <div className="flex flex-col justify-between p-8 sm:p-12 border-r border-border relative">
        <NexusLogo />

        <div className="max-w-md w-full mx-auto lg:mx-0 py-10">
          <div className="mb-8">
            <div className="nx-chip nx-chip-cyan mb-4">COMECE GRÁTIS · SEM CARTÃO</div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
              Crie sua conta na <span className="nx-text-gradient">Nexus Live</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Já tem uma?{" "}
              <Link to="/login" className="text-[hsl(var(--nx-cyan))] hover:underline" data-testid="register-goto-login">
                Entrar
              </Link>
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" required value={form.email} onChange={change("email")}
                  data-testid="register-email-input" autoComplete="email"
                  placeholder="voce@nexus.live"
                  className="pl-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Usuário</Label>
                <div className="relative">
                  <AtSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="username" required minLength={3} maxLength={32}
                    pattern="[a-zA-Z0-9_\.]+"
                    value={form.username} onChange={change("username")}
                    data-testid="register-username-input" autoComplete="username"
                    placeholder="kaelzin"
                    className="pl-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_name" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Nome exibido</Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="display_name" value={form.display_name} onChange={change("display_name")}
                    data-testid="register-displayname-input" placeholder="Kael Costa"
                    className="pl-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type={showPwd ? "text" : "password"} required minLength={6}
                  value={form.password} onChange={change("password")}
                  data-testid="register-password-input" autoComplete="new-password"
                  placeholder="mínimo 6 caracteres"
                  className="pl-10 pr-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]" />
                <button type="button" onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="register-toggle-password">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-xl nx-btn-blue border-0 font-semibold text-base"
              data-testid="register-submit-btn">
              {loading ? "criando…" : (<>Criar conta <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>

            <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
              Ao criar sua conta você concorda com os <span className="text-foreground">Termos de Uso</span> e a <span className="text-foreground">Política de Privacidade</span>.
            </p>
          </form>
        </div>

        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
          © {new Date().getFullYear()} Nexus Live · v1.0
        </p>
      </div>

      {/* RIGHT — Illustration */}
      <div className="hidden lg:block relative overflow-hidden bg-[hsl(var(--nx-panel))]">
        <div className="absolute inset-0 nx-grid-bg opacity-40" />
        <div className="absolute -top-40 -left-24 w-[520px] h-[520px] rounded-full bg-[hsl(var(--nx-cyan)/0.18)] blur-[120px]" />
        <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-[hsl(var(--nx-blue)/0.24)] blur-[100px]" />

        <div className="relative z-10 h-full flex flex-col p-10 xl:p-14">
          <div>
            <div className="nx-chip nx-chip-blue mb-4">
              <Rocket className="w-3.5 h-3.5" /> pronto em segundos
            </div>
            <h2 className="font-display font-bold text-3xl xl:text-[40px] leading-[1.05]">
              Sua tribo, seu <span className="nx-text-gradient">palco</span>,<br />
              suas regras.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-lg text-sm">
              Comece pequeno com um servidor entre amigos ou construa um universo com múltiplos canais, cargos, moderação e transmissões.
            </p>
          </div>

          {/* Preview: server card + roles */}
          <div className="mt-10 grid grid-cols-2 gap-6">
            <div className="nx-glass p-4 rounded-2xl">
              <div className="aspect-[16/9] rounded-xl bg-gradient-to-br from-[hsl(224,60%,22%)] via-[hsl(217,50%,16%)] to-[hsl(190,60%,20%)] mb-3 relative overflow-hidden">
                <div className="absolute inset-0 nx-scanline" />
                <div className="absolute top-3 left-3 nx-chip nx-chip-blue !py-1">Games</div>
              </div>
              <div className="font-display font-semibold text-lg">Guilda Nexus</div>
              <div className="text-xs text-muted-foreground">128 online · 362 membros</div>

              <div className="mt-4 space-y-1.5">
                {["# geral","# clips","🔊 sala 1"].map(c => (
                  <div key={c} className="flex items-center justify-between text-sm px-2 py-1 rounded-md hover:bg-white/5">
                    <span className="text-muted-foreground">{c}</span>
                    <span className="text-[10px] text-muted-foreground">▸</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="nx-glass p-4 rounded-2xl">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 font-semibold">Cargos</div>
              <div className="space-y-2">
                {[
                  { n: "Admin", c: "hsl(217,96%,60%)" },
                  { n: "Moderador", c: "hsl(190,92%,55%)" },
                  { n: "Streamer", c: "hsl(300,92%,62%)" },
                  { n: "Membro", c: "hsl(218,12%,62%)" },
                ].map(r => (
                  <div key={r.n} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <span className="w-2 h-2 rounded-full" style={{background: r.c}} />
                    <span className="text-sm">{r.n}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-[hsl(var(--nx-border))]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">Online — 7</div>
                <div className="flex -space-x-2">
                  {["hsl(217,96%,60%)","hsl(190,92%,55%)","hsl(40,96%,56%)","hsl(300,92%,62%)","hsl(145,62%,55%)"].map((c,i)=>(
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-[hsl(var(--nx-panel))]" style={{background: c}} />
                  ))}
                  <div className="w-8 h-8 rounded-full border-2 border-[hsl(var(--nx-panel))] bg-white/10 text-[10px] font-semibold grid place-items-center">
                    +2
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
